/**
 * @fileoverview marketing-shell's Spanish strings. Every leaf mirrors `en.js`; provenance per leaf
 * is in `_meta.js` (all machine-translated, carried from the marketing sites' own catalogs).
 * @package @puffin-core/marketing-shell
 *
 * @module @puffin-core/marketing-shell/strings/es
 */

export const MARKETING_SHELL_ES = Object.freeze({
  skipLink: 'Saltar al contenido principal',
  menu: Object.freeze({
    open: 'Abrir menú',
    close: 'Cerrar menú',
  }),
  ecosystemSwitcher: Object.freeze({
    label: 'Productos',
  }),
  localeSwitcher: Object.freeze({
    label: 'Idioma',
  }),
  cookieConsent: Object.freeze({
    title: 'Preferencias de cookies',
    message:
      'Utilizamos cookies para mejorar su experiencia y analizar el tráfico del sitio. Consulte nuestra',
    privacyLink: 'Política de privacidad',
    moreInfo: 'para más información.',
    decline: 'Rechazar',
    accept: 'Aceptar',
    settings: 'Configuración de cookies',
    gpcNotice:
      'Su navegador envía una señal de Control Global de Privacidad, por lo que la analítica está desactivada a menos que acepte aquí.',
  }),
  footer: Object.freeze({
    connect: 'Síganos',
    copyright: '© {year} {owner}. Todos los derechos reservados.',
    opensInNewTab: '(se abre en una pestaña nueva)',
    siteFooter: 'Pie de página del sitio de {appName}',
    privacyPolicy: 'Política de privacidad',
    termsOfService: 'Términos del servicio',
    contact: 'Contacto',
  }),
  readingProgress: Object.freeze({
    label: 'Progreso de lectura',
  }),
  relatedPosts: Object.freeze({
    heading: 'Artículos relacionados',
  }),
  tableOfContents: Object.freeze({
    label: 'Índice',
  }),
  demoRequestModal: Object.freeze({
    close: 'Cerrar',
  }),
  blogPagination: Object.freeze({
    label: 'Paginación del blog',
    previous: 'Anterior',
    next: 'Siguiente',
    pageOf: 'Página {page} de {total}',
    page: 'Página {page}',
  }),
  founderProfile: Object.freeze({
    signature: 'Firma: {name}',
  }),
  testimonials: Object.freeze({
    previous: 'Testimonio anterior',
    next: 'Testimonio siguiente',
    slides: 'Diapositivas de testimonios',
    goTo: 'Ir al testimonio {position} de {count}',
  }),
  contactForm: Object.freeze({
    honeypot: 'Sitio web (déjelo vacío)',
    submit: 'Enviar',
    submitting: 'Enviando…',
    formLabel: 'Formulario de contacto',
    referenceId: 'ID de referencia',
  }),
  pricing: Object.freeze({
    anchorsHeading: 'Empieza con un ancla',
    anchorsSubheading:
      'Cada cuenta empieza con una. Añade cualquier app a su lado, cualquier día; los cambios se prorratean en ambos sentidos.',
    includesHeading: '{anchor} incluye',
    freeAppFeature: '{app}, gratis',
    perUnitMonth: 'por {unit} al mes',
    perUnitYear: 'por {unit} al año',
    flatMonth: 'al mes',
    flatYear: 'al año',
    annualNote: '{monthly} al mes, facturado anualmente — {free} meses gratis',
    comingSoon: 'Próximamente',
    startTrial: 'Empiece su prueba gratis',
    startTrialOf: 'Empiece una prueba gratis de {app}',
    trialTerms:
      'Prueba gratis de {days} días en cada app, sin tarjeta. Cuando una prueba termina sin tarjeta, la app pasa a solo lectura: no se borra nada.',
    addOnsHeading: 'Añade lo que necesites',
    addOnsSubheading: 'Cada app tiene su propio precio y empieza con su propia prueba.',
    addOnsCaption: 'Apps que puede añadir junto a {anchor}',
    columnApp: 'App',
    columnPrice: 'Precio',
    columnTrial: 'Prueba',
    columnAction: 'Pruébala',
    trialDays: '{days} días, sin tarjeta',
    notYetOrderable: 'Aún no disponible para pedir',
    freeAppsHeading: 'Gratis con cualquier ancla',
    freeAppsBody: 'Vienen con cualquier ancla, sin costo.',
    cycleAria: 'Ciclo de facturación',
    cycleMonthly: 'Mensual',
    cycleAnnual: 'Anual — {free} meses gratis',
    processingRate:
      'Pagos con tarjeta: {rate}% + {fixed} por transacción, publicado e igual para todos.',
    exampleHeading: 'Lo que pagan tres tiendas',
    exampleCaption:
      '{anchor} en tres ubicaciones, una de ellas con cuatro cajas, con {apps} — antes de impuestos.',
    exampleCaptionAnchorOnly:
      '{anchor} en tres ubicaciones, una de ellas con cuatro cajas — antes de impuestos.',
    columnLine: 'Qué',
    columnMonthly: 'Mensual',
    columnAnnual: 'Anual',
    annualColumnNote: 'Anual es exactamente el precio de {months} meses por doce.',
    totalRow: 'Total',
    quoteLine: '{quantity} {units} · {amount} al mes',
    totalMonthly: '{amount} al mes',
    totalAnnual: '{amount} al año ({monthly} al mes)',
    loading: 'Cargando precios…',
    loadFailed: 'No pudimos cargar nuestros precios. Inténtelo de nuevo.',
    retry: 'Reintentar',
    jsonLdUnitText: 'por {unit} al mes',
    jsonLdProductDescription:
      '{app}, del catálogo de apps de la Colonia ParcelPuffin. Cada app empieza con una prueba gratis.',
  }),
  tenantSite: Object.freeze({
    nav: Object.freeze({
      label: 'Principal',
      home: 'Inicio',
      blog: 'Blog',
      products: 'Productos',
      customDesign: 'Diseño personalizado',
      cart: 'Carrito',
      signIn: 'Iniciar sesión',
    }),
    video: Object.freeze({
      play: 'Reproducir video: {title}',
      notice:
        'Al reproducirlo se carga el reproductor de {provider}, que puede instalar sus propias cookies.',
    }),
    page: Object.freeze({
      loading: 'Cargando…',
      loadFailed: 'No se pudo cargar esta página.',
      retry: 'Intentar de nuevo',
      notFoundTitle: 'Página no encontrada',
      notFoundBody: 'La página que busca no existe.',
    }),
    preview: Object.freeze({
      notice:
        'Vista previa: esta página incluye cambios sin publicar y no es visible para el público.',
      linkInvalidTitle: 'Este enlace de vista previa caducó o no es válido',
      linkInvalidBody: 'Pida un nuevo enlace de vista previa al autor de la página.',
    }),
    product: Object.freeze({
      loadingOne: 'Cargando producto…',
      loadingMany: 'Cargando productos…',
      view: 'Ver producto',
    }),
    locations: Object.freeze({
      visit: 'Visite {name}',
      directions: 'Cómo llegar',
      reviews: 'Leer reseñas',
      writeReview: 'Escribir una reseña',
      call: 'Llamar',
      email: 'Correo',
      address: 'Dirección',
      hours: 'Horario',
      services: 'Servicios',
      contactCta: 'Contáctenos',
      notFoundTitle: 'Tienda no encontrada',
      notFoundBody: 'Esta tienda no está en la lista o ha cerrado.',
      newTab: 'se abre en una pestaña nueva',
    }),
    tracking: Object.freeze({
      numberLabel: 'Número de seguimiento',
      numberHint: 'Tal como aparece en su recibo o etiqueta: letras, dígitos y espacios.',
      submit: 'Rastrear paquete',
      submitting: 'Buscando…',
      resultHeading: 'Paquete {number}',
      status: 'Estado',
      carrier: 'Transportista',
      parcel: 'Bulto {sequence} de {count}',
      shipped: 'Enviado',
      delivered: 'Entregado',
      lastUpdated: 'Última actualización',
      estimatedDelivery: 'Entrega estimada',
      progress: 'Progreso',
      noProgress: 'Aún no se han registrado escaneos.',
      source: Object.freeze({
        carrier: 'Actualizado con los escaneos del transportista; se refresca cada 30 minutos.',
        records: 'Según nuestros registros de envío.',
      }),
      scans: Object.freeze({
        unknown: 'Actualización del transportista',
        pre_transit: 'Etiqueta creada',
        in_transit: 'En tránsito',
        out_for_delivery: 'En reparto',
        available_for_pickup: 'Disponible para recoger',
        delivered: 'Entregado',
        return_to_sender: 'Devolviéndose al remitente',
        failure: 'Intento de entrega fallido',
        cancelled: 'Cancelado',
        error: 'El transportista informó un problema',
      }),
      carrierPage: 'Rastrear en el sitio del transportista',
      newTab: 'se abre en una pestaña nueva',
      again: 'Rastrear otro paquete',
      errors: Object.freeze({
        invalid:
          'Escriba el número de seguimiento tal como aparece: de 6 a 40 letras, dígitos y espacios.',
        notFound:
          'Ningún envío coincide con ese número de seguimiento. Revise el número e inténtelo de nuevo.',
        rateLimited: 'Demasiadas consultas. Espere un momento e inténtelo de nuevo.',
        captchaFailed: 'La verificación CAPTCHA falló. Inténtelo de nuevo.',
        network: 'Problema de red: revise su conexión e inténtelo de nuevo.',
        unknown: 'No pudimos consultarlo en este momento. Inténtelo de nuevo.',
      }),
      statuses: Object.freeze({
        pending: 'Recibido',
        processing: 'En preparación',
        awaiting_pickup: 'Esperando la recogida del transportista',
        shipped: 'Enviado',
        in_transit: 'En tránsito',
        delivered: 'Entregado',
        returned: 'Devuelto al remitente',
        cancelled: 'Cancelado',
        imported_unknown: 'Estado no disponible',
      }),
    }),
    shipping: Object.freeze({
      fromStore: 'Enviar desde',
      fromStoreAny: 'Elija una tienda',
      toPostal: 'Código postal de destino',
      postalHint: 'Un código postal de EE. UU., 5 dígitos (ZIP+4 también sirve).',
      weight: 'Peso',
      weightUnit: 'Unidad de peso',
      dimensions: 'Tamaño',
      length: 'Largo',
      width: 'Ancho',
      height: 'Alto',
      dimensionUnit: 'Unidad de tamaño',
      addressClass: 'Entrega en',
      submit: 'Estimar el envío',
      submitting: 'Calculando…',
      resultHeading: 'Tarifas estimadas',
      disclaimer:
        'Son estimaciones. El precio final se fija en el mostrador, donde pesamos y medimos su paquete.',
      noQuotes: 'Ningún servicio cotizó este paquete. Tráigalo y lo cotizamos en el mostrador.',
      notPriced: 'Servicios que no pudimos cotizar para este paquete: {count}.',
      perQuote: '{carrier} {service}',
      billedWeight: 'Facturado a {pounds} lb',
      again: 'Estimar otro paquete',
      units: Object.freeze({
        oz: 'oz',
        lb: 'lb',
        g: 'g',
        kg: 'kg',
        in: 'in',
        cm: 'cm',
      }),
      addressClasses: Object.freeze({
        residential: 'Una casa',
        commercial: 'Un negocio',
      }),
      errors: Object.freeze({
        store: 'Elija la tienda desde la que envía.',
        toPostal: 'Escriba el código postal al que envía.',
        weight: 'Escriba un peso mayor que cero.',
        dimensions: 'Escriba los tres lados, cada uno mayor que cero.',
        outOfRange:
          'Ese paquete excede lo que un transportista acepta (150 lb; 108 in por lado). Tráigalo y le ayudamos.',
        storeUnknown: 'Esa tienda no está disponible. Elija otra.',
        refused:
          'Ningún servicio de esa tienda puede llevar este paquete a esa dirección. Tráigalo y le ayudamos.',
        unavailable:
          'No podemos cotizar el envío en este momento. Inténtelo de nuevo o traiga su paquete.',
        rateLimited: 'Demasiadas estimaciones. Espere un momento e inténtelo de nuevo.',
        captchaFailed: 'La verificación CAPTCHA falló. Inténtelo de nuevo.',
        network: 'Problema de red: revise su conexión e inténtelo de nuevo.',
        unknown: 'No pudimos cotizarlo en este momento. Inténtelo de nuevo.',
      }),
    }),
    contact: Object.freeze({
      name: 'Nombre',
      email: 'Correo electrónico',
      phone: 'Teléfono',
      message: 'Mensaje',
      submit: 'Enviar mensaje',
    }),
    newsletter: Object.freeze({
      heading: 'Novedades de la tienda',
      emailLabel: 'Correo electrónico',
      consentText:
        'Quiero recibir por correo electrónico novedades y ofertas de esta tienda. Puedo cancelar la suscripción en cualquier momento: cada correo incluye un enlace para hacerlo.',
      submit: 'Suscribirme',
      submitting: 'Suscribiendo…',
      sentHeading: 'Revise su bandeja de entrada',
      sentBody:
        'Si esa dirección es nueva para nosotros, le enviamos un enlace de confirmación. No se envía nada hasta que lo abra.',
      again: 'Suscribir otra dirección',
      errors: Object.freeze({
        invalid: 'Escriba una dirección de correo electrónico válida.',
        consentRequired: 'Marque la casilla para aceptar recibir correos de esta tienda.',
        rateLimited: 'Demasiadas suscripciones. Espere un momento e inténtelo de nuevo.',
        captchaFailed: 'La verificación CAPTCHA falló. Inténtelo de nuevo.',
        unavailable: 'Las suscripciones no están disponibles en este sitio por ahora.',
        deliveryUnavailable:
          'No pudimos enviar el correo de confirmación en este momento. Inténtelo de nuevo en unos minutos.',
        network: 'Problema de red: revise su conexión e inténtelo de nuevo.',
        unknown: 'No pudimos suscribirle en este momento. Inténtelo de nuevo.',
      }),
    }),
    // DTOWNRETAIL-SITE-001 V5 — la lista de precios: «desde» es el precio más bajo entre las tiendas abiertas.
    priceList: Object.freeze({
      from: 'Desde {price}',
      at: 'Precios en {store}',
      chainNote:
        '«Desde» es el precio más bajo entre nuestras tiendas abiertas; la página de cada tienda muestra su precio exacto.',
    }),
    footer: Object.freeze({
      label: 'Pie de página del sitio',
      privacy: 'Privacidad',
      terms: 'Términos',
      disclosurePage: 'Divulgación',
      accessibility: 'Accesibilidad',
      privacyRequest: 'Solicitud de privacidad',
      explore: 'Explorar',
      connect: 'Síganos',
      poweredBy: 'Con tecnología de',
    }),
  }),
});
